
import { inject } from "@angular/core";
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { User } from "../models/user.model"; // Asegúrate de tener este modelo
import { AuthService } from "../services/auth.service"; // Asegúrate de tener este servicio
import to from "await-to-js";
import { lastValueFrom } from "rxjs";
import { isTokenExpired } from '../core/utils/is-token-expired'; // Asegúrate de tener esta utilidad

type UserState = {
    user: User | null;
    tokens: string | null;
    loading: boolean;
    error: string | null;
}

const initialState: UserState = {
    user: null,
    tokens: null,
    loading: false,
    error: null,
}

function sanitizeUser(user: User | null): User | null {
    if (!user) return null;

    const sanitizedUser = { ...user } as User & { walletPrivateKey?: string };
    delete sanitizedUser.walletPrivateKey;
    return sanitizedUser;
}

function loadUserFromLocalStorage(): UserState {
    const user = localStorage.getItem('user');
    const tokens = localStorage.getItem('tokens');
    const sanitizedUser = user ? sanitizeUser(JSON.parse(user)) : null;

    if (user) {
        localStorage.setItem('user', JSON.stringify(sanitizedUser));
    }

    if (user && tokens) {
        if (isTokenExpired(tokens)) {
            localStorage.removeItem('user');
            localStorage.removeItem('tokens');
            return initialState;
        }
        return {
            user: sanitizedUser,
            tokens,
            loading: false,
            error: null,
        };
    }
    return initialState;
}
export const UserStore = signalStore(
    { providedIn: 'root' },
    withState(loadUserFromLocalStorage()),
    withMethods(
        (store: any, authService = inject(AuthService)) => ({
            async login(email: string, password: string) {
                patchState(store, { loading: true, error: null });

                const [err, response] = await to(lastValueFrom(authService.login({ email, password })));

                if (err) {
                    patchState(store, { error: err.message, loading: false });
                    return;
                }

                if (response) {
                    const { user, tokens } = response;
                    const sanitizedUser = sanitizeUser(user);
                    patchState(store, { user: sanitizedUser, tokens, loading: false });

                    localStorage.setItem('user', JSON.stringify(sanitizedUser));
                    localStorage.setItem('tokens', tokens);
                } else {
                    patchState(store, { error: 'Login failed', loading: false });
                }
            },

            async logOut() {
                patchState(store, {
                    user: null,
                    tokens: null,
                    error: null,
                    loading: false
                });

                localStorage.removeItem('user');
                localStorage.removeItem('tokens');
            },

            updateUser(updatedUser: User | null) {
                const sanitizedUser = sanitizeUser(updatedUser);
                patchState(store, { user: sanitizedUser, loading: false });
                localStorage.setItem('user', JSON.stringify(sanitizedUser));
            }
        })
    )
);
